<template>
  <!-- Collapsible Content -->
  <div v-bind="$attrs" :class="['sidemenu', { expanded: isSidemenu, 'is-phone': isPhone }, $attrs.class]">
    <div v-if="workflowModel.active !== 0 && productTypeId === 1" class="sidemenu-header p-3">
      <!-- Desktop/Tablet: new indicator -->
      <template v-if="!isPhone">
        <step-line-indicator
          :currentStep="workflowModel.active"
          :steps="workflowSteps"
          @change-step="(id: number) => setStep(id)"
        />
      </template>

      <!-- Phone: legacy compact workflow header preserved -->
      <template v-else>
        <p class="ml-1 mb-1 fw-bold" style="font-size: 0.75rem;">
          {{ workflowModel.active + '/3: ' + (workflowSteps[workflowModel.active - 1] ?? '') }}
        </p>
        <div class="workflow">
          <a
            v-for="(step, index) in steps.slice(0, 3)"
            :key="step.id"
            class="workflow-line"
            :class="{ active: workflowModel.active - 1 >= index }"
            @click="setStep(step.id)"
          ></a>
        </div>
      </template>
    </div>

    <div v-if="workflowModel.active !== 0 && productTypeId !== 1" class="sidemenu-header title mt-3 p-4">
      {{ steps[1]?.description ?? '' }}
    </div>

    <div v-if="isProductTypeWithVariants(this.productTypeId) === false" class="row sidemenu-body pt-1">
      <div v-if="workflowModel.active !== 0">
        <ProductSettings
          v-if="selectedModel !== null"
          :key="selectedModel.product.productId"
          :product="selectedModel.product"
          :is-authenticated="authentication.isAuthenticated"
          :order="order"
          :video-metadata="videoMetadata"
          :hide-info="productParameterEmbossing"
          @zip-changed="(zip: any) => $emit('zipChanged', zip)"
          @product-updated="updateProductListState"
          @update-properties="
            (newProperties: OrderRequestData) => {
              order = { ...order, ...newProperties };
            }
          "
          @is-valid="setFormValid"
          ref="productSettingsRef"
        />
      </div>
    </div>

    <!-- Footer for PRODUCT TYPE != 1 (sticky) -->
    <div v-if="productTypeId !== 1" class="sidemenu-footer">
      <el-button class="btn-stepper" type="primary" v-show="workflowModel.active !== 0" @click="handleGenerate(selectedModel!)">
        {{ $t('views.configurator.generate') }}
      </el-button>
      <el-button class="btn-stepper" type="primary" v-if="workflowModel.active !== 0 && generated" @click="goToOrder">
        {{ $t('views.configurator.order') }}
      </el-button>
    </div>

    <!-- PRODUCT TYPE == 1 -->
    <div v-else>
      <!-- STEP 1 -->
      <div
        v-if="workflowModel.active === 1"
        :key="'step1-' + (isPhone ? 'phone' : 'desktop')"
        v-loading="productsLoading"
        element-loading-background="transparent"
        class="row sidemenu-body pt-1"
        :class="{ 'variants-open': isPhone && !!selectedCategory }"
      >
        <!-- PHONE layout -->
        <template v-if="isPhone">
          <!-- Category chips (Covers only) -->
          <div v-if="productTypeId === 1" class="phone-category-chips">
            <button
              v-for="(data, category) in groupedProducts"
              :key="category"
              class="chip"
              :class="{ 'chip--active': selectedCategory === category }"
              @click="selectCategory(category)"
              type="button"
            >
              {{ category }}
            </button>
          </div>

          <!-- Title -->
          <p class="title">
            {{
              productTypeId === 1
                ? (selectedCategory ? selectedCategory + ' ' + $t('views.configurator.variants') : $t('views.configurator.productType'))
                : $t('views.configurator.productType')
            }}
          </p>

          <!-- Phone cards -->
          <div class="phone-card-list">
            <!-- Non-cover products -->
            <template v-if="productTypeId !== 1">
              <article
                v-for="model in productList"
                :key="model.modelId"
                class="phone-card"
                :class="{ 'phone-card--active': model.product.productId === selectedModel?.product.productId }"
              >
                <button class="phone-card__body" type="button" @click="handleProductSelected(model)">
                  <preview-renderer-img
                    :meshes="[{ thumbnail: model.product?.name, thumbnailUrl: convertToUrl(model) }]"
                    img-size="5"
                  />
                  <div class="phone-card__text">
                    <h3 class="phone-card__title">{{ model.product.name }}</h3>
                    <p class="phone-card__subtitle">
                      {{ getSubtitle(model.product.productTypeAttribute!, model.product.name) }}
                    </p>
                    <p class="phone-card__desc">{{ getProductDescription(model.product.description, 120) }}</p>
                  </div>
                </button>
              </article>
            </template>

            <!-- Covers: category overview -->
            <template v-else-if="!selectedCategory">
              <article
                v-for="(data, category) in groupedProducts"
                :key="category"
                class="phone-card"
                :class="{ 'phone-card--active': selectedCategory === category }"
              >
                <button class="phone-card__body" type="button" @click="selectCategory(category)">
                  <preview-renderer-img
                    :meshes="[{ thumbnail: data.models[0]?.product?.name, thumbnailUrl: convertToUrl(data.models[0]) }]"
                    img-size="5"
                  />
                  <div class="phone-card__text">
                    <h3 class="phone-card__title">{{ category }}</h3>
                    <p class="phone-card__desc">{{ data.description }}</p>
                  </div>
                </button>
              </article>
            </template>

            <!-- Covers: variants of selected category (HORIZONTAL SCROLL) -->
            <template v-else>
              <div class="variant-strip" role="list">
                <article
                  v-for="model in selectedCategoryProducts"
                  :key="model.product.productId"
                  class="phone-card phone-card--variant"
                  :class="{ 'phone-card--active': model.product.productId === selectedModel?.product.productId }"
                  role="listitem"
                >
                  <button class="phone-card__body phone-card__body--row" type="button" @click="handleProductSelected(model)">
                    <div class="variant-thumb">
                      <preview-renderer-img
                        :meshes="[{ thumbnail: model.product.name, thumbnailUrl: convertToUrl(model) }]"
                        img-size="5"
                      />
                    </div>
                    <div class="phone-card__text">
                      <h3 class="phone-card__title">{{ model.variantName }}</h3>
                      <p class="phone-card__subtitle">
                        {{ getSubtitle(model.product.productTypeAttribute!, model.product.name) }}
                      </p>
                      <p class="phone-card__desc">{{ getProductDescription(model.product.description, 80) }}</p>
                    </div>
                  </button>
                </article>
              </div>
            </template>
          </div>
        </template>

        <!-- DESKTOP layout -->
        <template v-else>
          <p class="title">{{ $t('views.configurator.productType') }}</p>
          <div class="product-grid justify-content-center">
            <template v-if="productTypeId !== 1">
              <div v-for="model in productList" :key="model.modelId" class="product-card__wrapper">
                <product-card
                  :show-delete-icon="authentication.isAuthenticated && isUserSupplier"
                  :active="model.product.productId === selectedModel?.product.productId"
                  :title="model.product.name"
                  :primary-button-text="
                    model.product.type.name.toLowerCase() === 'covers'
                      ? $t('views.customize.workflow.productVariants.productCardPrimaryButtonOption3')
                      : $t('views.customize.workflow.productVariants.productCardPrimaryButtonOption1')
                  "
                  @delete-product="onDeleteProduct(model)"
                  @button-clicked="handleProductSelected(model)"
                >
                  <preview-renderer-img
                    :meshes="[{ thumbnail: model.product?.name, thumbnailUrl: convertToUrl(model) }]"
                    img-size="5"
                  />
                </product-card>
              </div>
            </template>

            <!-- Cover Types -->
            <template v-else>
              <div v-for="(data, category) in groupedProducts" :key="category" class="product-card__wrapper">
                <product-card
                  :show-delete-icon="authentication.isAuthenticated && isUserSupplier"
                  :title="category"
                  :description="data.description"
                  :active="selectedCategory === category"
                  :primary-button-text="$t('views.customize.workflow.productVariants.productCardPrimaryButtonOption1')"
                  @button-clicked="selectCategory(category)"
                >
                  <preview-renderer-img
                    :meshes="[{ thumbnail: data.models[0]?.product?.name, thumbnailUrl: convertToUrl(data.models[0]) }]"
                    img-size="5"
                  />
                </product-card>
              </div>
            </template>
          </div>

          <!-- Variants of Selected Cover Type -->
          <p v-if="productTypeId === 1 && selectedCategory" class="title mt-2">
            {{ selectedCategory + ' ' + $t('views.configurator.variants') }}
          </p>
          <div v-if="productTypeId === 1 && selectedCategory" class="product-grid justify-content-center">
            <div
              v-for="model in selectedCategoryProducts"
              :key="model.product.productId"
              class="product-card__wrapper col-lg-6 col-md-6 col-sm-12 m-0 p-0"
            >
              <product-card
                :title="model.variantName"
                :description="getProductDescription(model.product.description, 150)"
                :subtitle="getSubtitle(model.product.productTypeAttribute!, model.product.name)"
                :active="model.product.productId === selectedModel?.product.productId"
                @button-clicked="handleProductSelected(model)"
              >
                <preview-renderer-img
                  :meshes="[{ thumbnail: model.product.name, thumbnailUrl: convertToUrl(model) }]"
                  img-size="5"
                />
              </product-card>
            </div>
          </div>
        </template>
      </div>

      <!-- STEP 2 -->
      <div v-if="workflowModel.active === 2" class="row sidemenu-body">
        <ProductSettings
          v-if="selectedModel != null"
          :key="selectedModel.product.productId"
          :product="selectedModel.product"
          :is-authenticated="authentication.isAuthenticated"
          :order="order"
          :video-metadata="videoMetadata"
          :hide-info="productParameterEmbossing"
          @zip-changed="(zip: any) => $emit('zipChanged', zip)"
          @product-updated="handleProductUpdated"
          @update-properties="
            (newProperties: OrderRequestData) => {
              order = { ...order, ...newProperties };
            }
          "
          @is-valid="setFormValid"
          ref="productSettingsRef"
        />
      </div>

      <!-- STEP 3 -->
      <div v-if="workflowModel.active === 3" class="row sidemenu-body" ref="colorAndDesignContainer">
        <!-- PHONE-ONLY layout -->
        <template v-if="isPhone">
          <!-- Upper/Lower part chips -->
          <div class="part-supermenu" role="tablist" aria-label="Selected part">
            <button
              v-for="(mesh, i) in meshCountItemList"
              :key="mesh.meshTreeDataItem.id ?? `part-${i}`"
              type="button"
              class="part-chip"
              :class="{
                'part-chip--active':
                  selectedPart?.id != null && selectedPart.id === mesh.meshTreeDataItem.id
              }"
              @click="$emit('partSelected', mesh.meshTreeDataItem)"
            >
              {{ mesh.meshTreeDataItem.name }}
            </button>
          </div>

          <!-- Horizontal picker: colors / textures / embossing -->
          <div
            class="picker-strip"
            role="list"
            aria-orientation="horizontal"
            ref="pickerStrip"
          >
            <mesh-colors-and-design
              :mesh-engravings="meshEngravings"
              :mesh-colors="meshColors"
              :mesh-texture-colors="meshTextureColors"
              :mesh-embossing-colors="meshEmbossingColors"
              :selected-product-name="selectedModel?.product.name"
              :selected-engraving="selectedEngraving"
              :selected-texture="selectedTexture"
              :selected-color="selectedColor"
              :selected-embossing-color="selectedEmbossingColor"
              :selected-texture-color="selectedTextureColor"
              :mesh-tree-data="meshTreeData"
              @handle-select-embossing="(id:any)=>{ storeScrollPosition(); $emit('handle-select-embossing', id); this.restoreScrollPosition(); }"
              @colorize-selected-mesh="(c:any)=>{ storeScrollPosition(); $emit('mesh-color-selected', c); this.restoreScrollPosition(); }"
              @colorize-embossing-mesh="(c:any)=>{ storeScrollPosition(); $emit('mesh-embossing-color-selected', c); this.restoreScrollPosition(); }"
              @colorize-texture-mesh="(c:any)=>{ storeScrollPosition(); $emit('mesh-texture-color-selected', c); this.restoreScrollPosition(); }"
            />
          </div>
        </template>

        <!-- DESKTOP/TABLET layout -->
        <template v-else>
          <div v-if="meshCountItemList.length > 1">
            <el-collapse v-model="activeName" @update:model-value="handleAccordionChange" accordion>
              <el-collapse-item
                class="cd-collapse"
                v-for="(mesh, index) in meshCountItemList"
                :title="mesh.meshTreeDataItem.name"
                :key="mesh.meshTreeDataItem.id"
                :name="index.toString()"
                @click="$emit('partSelected', mesh.meshTreeDataItem)"
              >
                <mesh-colors-and-design
                  :mesh-engravings="meshEngravings"
                  :mesh-colors="meshColors"
                  :mesh-texture-colors="meshTextureColors"
                  :mesh-embossing-colors="meshEmbossingColors"
                  :selected-product-name="selectedModel?.product.name"
                  :selected-engraving="selectedEngraving"
                  :selected-texture="selectedTexture"
                  :selected-color="selectedColor"
                  :selected-embossing-color="selectedEmbossingColor"
                  :selected-texture-color="selectedTextureColor"
                  :mesh-tree-data="meshTreeData"
                  @handle-select-embossing="(fileTextureId: any) => { storeScrollPosition(); $emit('handle-select-embossing', fileTextureId); this.restoreScrollPosition(); }"
                  @colorize-selected-mesh="(color: any) => { storeScrollPosition(); $emit('mesh-color-selected', color); this.restoreScrollPosition(); }"
                  @colorize-embossing-mesh="(color: any) => { storeScrollPosition(); $emit('mesh-embossing-color-selected', color); this.restoreScrollPosition(); }"
                  @colorize-texture-mesh="(color: any) => { storeScrollPosition(); $emit('mesh-texture-color-selected', color); this.restoreScrollPosition(); }"
                />
              </el-collapse-item>
            </el-collapse>
          </div>
          <div v-else>
            <mesh-colors-and-design
              :mesh-engravings="meshEngravings"
              :mesh-colors="meshColors"
              :mesh-texture-colors="meshTextureColors"
              :mesh-embossing-colors="meshEmbossingColors"
              :selected-product-name="selectedModel?.product.name"
              :selected-engraving="selectedEngraving"
              :selected-texture="selectedTexture"
              :selected-color="selectedColor"
              :selected-embossing-color="selectedEmbossingColor"
              :selected-texture-color="selectedTextureColor"
              :mesh-tree-data="meshTreeData"
              @handle-select-embossing="(fileTextureId: any) => { storeScrollPosition(); $emit('handle-select-embossing', fileTextureId); this.restoreScrollPosition(); }"
              @colorize-selected-mesh="(color: any) => { storeScrollPosition(); $emit('mesh-color-selected', color); this.restoreScrollPosition(); }"
              @colorize-embossing-mesh="(color: any) => { storeScrollPosition(); $emit('mesh-embossing-color-selected', color); this.restoreScrollPosition(); }"
              @colorize-texture-mesh="(color: any) => { storeScrollPosition(); $emit('mesh-texture-color-selected', color); this.restoreScrollPosition(); }"
            />
          </div>
        </template>
      </div>

      <!-- Stepper footer (outside .sidemenu-body for sticky) -->
      <div class="sidemenu-footer">
        <el-button class="btn-stepper" type="primary" :disabled="workflowModel.active <= 1" v-show="workflowModel.active > 1" @click="previousStep">
          {{ $t('views.configurator.previous') }}
        </el-button>
        <el-button class="btn-stepper" type="primary" v-if="workflowModel.active !== steps.length" :disabled="workflowModel.active > steps.length" @click="nextStep">
          {{ $t('views.configurator.next') }}
        </el-button>
        <el-button class="btn-stepper" type="primary" v-if="workflowModel.active === steps.length" @click="nextStep">
          {{ $t('views.configurator.order') }}
        </el-button>
      </div>
    </div>
  </div>

  <!-- Toggle Button -->
  <button class="collapse-btn" type="button" @click="toggleSidemenu">
    <el-icon :size="13" style="margin-top: 5px">
      <ArrowRightBold v-if="!isSidemenu" />
      <ArrowLeftBold v-else />
    </el-icon>
  </button>
</template>

<script lang="ts">
import { Vue, Component, Prop, Ref, Watch } from 'vue-facing-decorator';
import {
  deleteFileFromAzure,
  deleteFileMetadataToAzureByProudctId,
  fetchEmailTemplate,
  generateIDNumber,
  getFileMetadataToAzureByProudctId,
  TableField,
} from '@/utils/file';
import { WorkflowModel } from '@/types/ui/WorkflowModel';
import Auth from '@/services/api/auth';
import ProductCard from '../layout/sidebar/ProductCard.vue';
import { Model2 } from '@/types/api/Model/Model';
import { ElMessageBox, ElMessage } from 'element-plus';
import * as modelService from '@/services/api/modelService';
import * as FormBuilderService from '@/services/api/formBuilderService';
import * as UserService from '@/services/api/userService';
import { cloneDeep } from '@apollo/client/utilities';
import { Modal } from 'bootstrap';
import * as LayoutUtility from '@/utils/layout';
import PreviewRendererImg from '@/components/features/previewRenderer/PreviewRendererImg.vue';
import { CustomizeProgress } from '@/types/enum/workflow';
import * as THREEEnum from '@/types/enum/three';
import * as TemplateService from '@/services/api/templateService';
import { ProductQuality } from '@/types/enum/template';
import { RGB } from '@/types/api/Utility/RGB';
import { File as UtilityFile } from '@/types/api/Utility/File';
import { Component as Component1 } from '@/types/api/Model/Component';
import { Reseller } from '@/types/api/User/Reseller';
import OrderForm from './OrderForm.vue';
import { SupportedValues } from '@/types/api/Utility/SupportedValues';
import { useProductStore } from '@/stores/productStore';
import { MeshTreeData, MeshTreeDataGroupItem, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import { OrderRequestData, OrderTemplate } from '@/services/api/orderService';
import { ProductColors, EmbossingColors } from '@/services/api/productDbService';
import { BodySide, ZipType } from '@/types/enum/order';
import { FileType3D, getFileType, FileTypeVideo } from '@/types/enum/upload';
import * as THREE from 'three';
import * as ProductDbService from '@/services/api/productDbService';
import ProductSettings from '../../components/configurator/ProductSettings.vue';
import i18n from '@/i18n';
import { Body_Attribute } from '@/types/api/Patient/BodyAttribute';
import MeshColorsAndDesign from '../../components/configurator/MeshColorsAndDesign.vue';
import PartSelection from '@/components/features/selection/PartSelection.vue';
import { useRoute, useRouter } from 'vue-router';
import { RGB2 } from '@/types/api/Utility/RGB2';
import { Role } from '@/types/enum/user';
import { reactive, ref } from 'vue';
import { Product } from '@/types/api/Model/Product';
import CustomizeOverview from './CustomizeOverview.vue';
import { useUserStore } from '@/stores/userStore';
import { useComponentSettingStore } from '@/utils/componentSettings';
import StepLineIndicator from '../ui-elements/StepLineIndicator.vue';
import { Product_Type_Attribute } from '@/types/api/Model/ProductTypeAttribute';

interface ImageData {
  uuid: string;
  name: string;
  url: string;
  thumbnailUrl: string;
}
interface Embossing {
  name: string;
  colors: string[];
}
interface MeshConfig {
  filename: string;
  colors: { [name: string]: string[] } | undefined;
  embossing: Embossing[] | undefined;
  wtransfer: string[] | undefined;
  thumbnail: string;
}
interface ProductConfig {
  type: string;
  name: string;
  thumbnail: string;
  meshes: MeshConfig[];
}

@Component({
  components: {
    ProductCard,
    PreviewRendererImg,
    MeshColorsAndDesign,
    ProductSettings,
    OrderForm,
    Modal,
    PartSelection,
    CustomizeOverview,
    StepLineIndicator
  },
  emits: [
    'productSelected',
    'partSelected',
    'zipChanged',
    'handle-select-embossing',
    'mesh-color-selected',
    'mesh-embossing-color-selected',
    'mesh-texture-color-selected',
    'abortCurrentLoading',
  ],
})
export default class ConfiguratorMenu extends Vue {
  @Prop({ default: null }) productTypeId!: number | null;
  @Prop({ default: null }) supplierUrl!: string | null;
  @Prop() workflowModel!: WorkflowModel<CustomizeProgress>;
  @Prop() order!: OrderRequestData;
  @Prop() orderTemplate!: OrderTemplate;

  @Prop() selectedEmbossingColor!: RGB2;
  @Prop() selectedColor!: RGB2;
  @Prop() selectedTextureColor!: RGB2;
  @Prop() selectedEngraving!: number;
  @Prop() selectedTexture!: number;

  @Prop({ default: null }) productId!: number | null;
  @Prop({ default: null }) embossingId!: number | number[] | null;
  @Prop({ default: null }) wtransferId!: number | number[] | null;
  @Prop({ default: null }) colorId!: number | number[] | null;
  @Prop({ default: null }) embossingColorId!: number | number[] | null;
  @Prop({ default: [] }) meshEngravings!: SupportedValues[];
  @Prop() meshEmbossingColors!: RGB2[];
  @Prop() meshColors!: RGB2[];
  @Prop() meshTextureColors!: RGB2[];
  @Prop() meshEditorLoadingProps!: boolean;

  @Prop() meshTreeData!: MeshTreeData;

  settingStore = useComponentSettingStore();

  get selectedPart(): MeshTreeDataItem | null {
    return this.meshTreeData?.getSelection()[0] || null;
  }
  get selectedPartIndex(): string | null {
    if (!this.selectedPart) return null;
    const index = this.meshCountItemList.findIndex((mesh) => mesh.meshTreeDataItem.id === this.selectedPart?.id);
    return index !== -1 ? index.toString() : null;
  }

  isProductTypeWithVariants(productTypeId: number | null): boolean{
    let hasVariants = false;
    hasVariants = this.isTypeCover(productTypeId)
    if(hasVariants === false){
      hasVariants =this.isTypeBrace(productTypeId)
    }
    return hasVariants;
  }
  
  isTypeCover(productTypeId: number | null): boolean{
    return productTypeId === 1;
  }

    isTypeBrace(productTypeId: number | null): boolean{
    return productTypeId === 215;
  }

  /** Watch for changes in the selected part and update the accordion */
  @Watch('selectedPartIndex', { immediate: true })
  onSelectedPartIndexChange(newIndex: string | null, oldIndex: string | null) {
    if (!this.accordionInitialized) {
      this.activeName = '';
      return;
    }
    if (newIndex !== null && newIndex !== oldIndex && !this.userClosedAccordion) {
      this.activeName = newIndex;
    }
  }

  handleAccordionChange(val: string) {
    this.accordionInitialized = true;
    this.userClosedAccordion = val === '';
    this.activeName = val;
  }

  get routeStep(): number {
    return Number(this.route.query.sp) || 1;
  }
  @Watch('routeStep')
  onRouteStepChange(routeStep: number) {
    if (routeStep !== this.workflowModel.active) {
      this.setStep(1);
    }
  }

  handleProductUpdated(payload: { productId: number; name: string }) {
    this.updateProductListState(payload);
  }

  static inheritAttrs = false;

  ref = ref(null);
  route = useRoute();
  router = useRouter();
  selectedBodyAttribute: Body_Attribute | null = null;
  meshCountItemList: MeshTreeDataGroupItem[] = [];
  activeName = '';
  userClosedAccordion = false;
  accordionInitialized = false;
  colorAndDesignContainer: HTMLElement | null = null;
  private lastScrollPosition = 0;

  // wheel->horizontal binding (desktop only)
  private _wheelBound: boolean = false;
  @Ref() readonly pickerStrip!: HTMLDivElement | null;

  isSidemenu = true;
  authentication = Auth.getInstance();
  productStore = useProductStore();
  userStore = useUserStore();
  secondStepValid = false;

  // props/state
  productTypes = this.productStore.productTypes;
  productConfigurationDB: ProductConfig[] = [];
  productDB: ProductDbService.ProductData[] = [];
  selectedProductId = '';
  selectedEmbossingContent = '';
  selectedModelId: number | null = null;
  selectedModel: Model2 | null = null;
  colors: { [name: string]: string[] } = ProductColors;
  embossingDB: ImageData[] = [];
  wtransferDB: ImageData[] = [];
  fullscreenLoading = false;
  productsLoading = false;
  meshEditorLoading = false;
  footerLoading = false;
  currentPage = 1;
  dataLoaded = false;
  width = window.innerWidth;
  lastUpdateDB = -1;
  transparentColor = '#d3d3d3';
  transparentColorRgb = { name: 'none', red: 211, green: 211, blue: 211 } as RGB;
  blankFileName = 'blank';
  automaticSize = false;
  selectedProductQuality = ProductQuality.low.toString();
  sendingOrder = false;

  zipperColors: RGB[] = [
    { name: 'white', red: 255, green: 255, blue: 255 } as RGB,
    { name: 'beige', red: 242, green: 231, blue: 191 } as RGB,
    { name: 'black', red: 40, green: 42, blue: 42 } as RGB,
    { name: 'gray', red: 136, green: 140, blue: 136 } as unknown as RGB,
  ];

  colorListLoading = new Map<string, boolean>();
  embossingListLoading = new Map<string, boolean>();
  wtransferListLoading = new Map<string, boolean>();
  sizeListLoading = new Map<string, boolean>();

  abortController = new AbortController();

  modelList: Model2[] = [];
  selectedScan: UtilityFile | null = null;
  selectedSkeleton: UtilityFile | null = null;

  loadingOfferPdf = false;

  loadedScan: Component1 | null = null;

  reseller: Reseller | null = null;
  isUserSupplier = false;
  filteredBalconyModels: Model2[] | null = null;
  filteredFenceModels: Model2[] | null = null;
  filteredLighModels: Model2[] | null = null;
  filteredBathtubModels: Model2[] | null = null;

  BodySide = BodySide;
  FileType = FileType3D;
  ZipType = ZipType;
  ProductQuality = ProductQuality;
  getFileType = getFileType;
  productList: Model2[] = [];
  suppliersWithHiddenBackArrow = ['wjtortho'];
  hideBackArrow = false;

  THREEEnum = THREEEnum;

  embossingColors: string[] = EmbossingColors;

  screenshotSrc: string[] = [];

  private productParameterModalDialog: Modal | null = null;
  private orderModal: Modal | null = null;
  private isLoadingButtonPartList = false;
  private videoMetadata: TableField = {
    productId: '',
    filename: '',
    fileType: FileTypeVideo.WEBM,
    fileUrl: '',
  };
  productParameterEmbossing = false;
  isLoadingDownloadObject = false;

  additionalSections = [{ title: 'Design & Color' }, { title: 'Complete your Order' }];

  steps = [
    { id: 1, description: i18n.global.t('views.configurator.step1') },
    { id: 2, description: i18n.global.t('views.configurator.step2') },
    { id: 3, description: i18n.global.t('views.configurator.step3') },
    { id: 4, description: i18n.global.t('views.configurator.order') },
    { id: 5, description: i18n.global.t('views.configurator.orderConfirm') },
  ];

  showProductTypes = false;

  Configurator: unknown;
  otherClassInstance: unknown;

  generated = false;

  // Phone handling
  isPhone = false;
  get isPhoneMq() { return '(max-width: 480px)'; }

  handleSidebarItemClick(item: { label: string; show: boolean; src: string }): void {
    if (item.label === i18n.global.t('views.configurator.home')) {
      this.goHome();
    }
  }

  updateMeshCount(): void {
    this.meshTreeData.getPartItemList()
      .then(list => {
        this.meshCountItemList = list.filter(item => item.meshTreeDataItem.name !== 'Reißverschluss');
      });
  }

  goHome(): void {
    this.setStep(1);
    const activeStep: number = this.workflowModel.active;
    this.router.push({
      path: this.route.path,
      query: { ...this.route.query, sp: activeStep.toString() },
    });
  }

  getCompanyDomain() {
    try {
      if (this.userStore.domain) {
        const urlSuffix = this.userStore.domain.urlSuffix;
        return urlSuffix.charAt(0).toUpperCase() + urlSuffix.slice(1);
      } else {
        return '';
      }
    } catch (error) {
      return error;
    }
  }

  get workflowSteps() {
    return this.steps.slice(0, 3).map(({ description }) => description);
  }

  selectedCategory: string | null = null;

  get categoryMappings() {
    return {
      's-cover': { name: 'S-Flex', description: i18n.global.t('views.configurator.softStandard') },
      'u-flex': { name: 'U-Flex', description: i18n.global.t('views.configurator.softIndividual') },
      'c-flex': { name: 'C-Flex', description: i18n.global.t('views.configurator.softIndividual') },
      'x-cover': { name: 'X-Cover', description: i18n.global.t('views.configurator.hardFlexIndividual') },
      'm-cover': { name: 'M-Cover', description: i18n.global.t('views.configurator.flexIndividual') },
      'prothesenschaft': { name: i18n.global.t('views.configurator.prostheticSocket'), description: i18n.global.t('views.configurator.prostheticSocket') },
      'korsett': { name: i18n.global.t('views.configurator.brace'), description: i18n.global.t('views.configurator.brace') },
    };
  }

  get variantMappings() {
    return {
      oberschenkel: i18n.global.t('views.configurator.thigh'),
      unterschenkel: i18n.global.t('views.configurator.lowerLeg'),
    };
  }

  subtitleRules: { match: RegExp; translationKey: string }[] = [
    { match: /x-?cover.*flex/i, translationKey: 'views.configurator.xCoverFlex' },
    { match: /x-?cover/i,       translationKey: 'views.configurator.xCoverHard' },
  ];

  getSubtitle(attribute: Product_Type_Attribute, name: string) {
    if (attribute) {
      return this.$t(`enum.productTypeAttributes.${attribute.productTypeAttributeName.toLowerCase()}`);
    } else if (name) {
      for (const rule of this.subtitleRules) {
        if (rule.match.test(name)) {
          return i18n.global.t(rule.translationKey);
        }
      }
    }
    return '';
  }

  get groupedProducts() {
    return this.productList.reduce((acc, model) => {
      const productName = model.product.name.toLowerCase();
      const mainCategory = Object.keys(this.categoryMappings).find((key) => productName.includes(key));
      if (mainCategory) {
        const { name, description } = this.categoryMappings[mainCategory as keyof typeof this.categoryMappings];
        if (!acc[name]) acc[name] = { description, models: [] };
        acc[name].models.push(model);
      }
      return acc;
    }, {} as Record<string, { description: string; models: Model2[] }>);
  }

  getCoverCategory(productName: string) {
    const category = Object.keys(this.categoryMappings).find((key) => productName.toLowerCase().includes(key));
    if (category) {
      this.selectCategory(this.categoryMappings[category as keyof typeof this.categoryMappings].name);
    }
  }

  getProductDescription(description: string, limit = 30) {
    if (!description) return;
    return description.length > limit ? description.substring(0, limit) + '...' : description;
  }

  get selectedCategoryProducts() {
    if (!this.selectedCategory) return [];
    return (
      this.groupedProducts[this.selectedCategory]?.models.map((model) => {
        const variantKey = Object.keys(this.variantMappings).find((key) => model.product.name.toLowerCase().includes(key));
        const variantName = variantKey && (variantKey as keyof typeof this.variantMappings) in this.variantMappings
          ? this.variantMappings[variantKey as keyof typeof this.variantMappings]
          : model.product.name;

        return { ...model, variantName } as Model2 & { variantName: string };
      }) || []
    );
  }

  get isButtonNextDisabled() {
    if (this.workflowModel.active === 1) {
      return !(this.selectedCategory && this.selectedModel && !this.meshEditorLoadingProps);
    }
    if (this.workflowModel.active === 2) {
      if (!this.selectedModel) return true;
      const { modelId } = this.selectedModel;
      const findProductVariant = this.selectedCategoryProducts.find(item => item.modelId === modelId);
      const { variantName } = findProductVariant as Model2 & { variantName: string };
      const { kneeJoint, bodySide, depth, heightWithoutDeduction, calfCircumferenceWithoutDeduction } = this.order;

      if (variantName?.toLowerCase() === 'lower leg') {
        return !(bodySide.length > 0 && depth > 0 && heightWithoutDeduction > 0 && calfCircumferenceWithoutDeduction > 0);
      }
      return !(kneeJoint.length > 0 && bodySide.length > 0 && depth > 0 && heightWithoutDeduction > 0 && calfCircumferenceWithoutDeduction > 0);
    }
    if (this.workflowModel.active === 3) return false;
    return this.workflowModel.active > this.steps.length;
  }

  selectCategory(category: string) {
    this.selectedCategory = category;
  }

  handleProductSelected(model: Model2) {
    this.accordionInitialized = false;
    if (this.meshEditorLoadingProps) this.$emit('abortCurrentLoading');
    this.$emit('productSelected', model);
    this.selectedModel = model;
    this.secondStepValid = false;
    const modelId = model.modelId.toString();
    localStorage.setItem('mlid', modelId);

    this.updateMeshCount();
    this.router.push({ query: { ...this.route.query, mlid: modelId } });
  }

  get meshHeight() { return this.order.heightWithoutDeduction; }
  set meshHeight(value: number) { this.order.heightWithoutDeduction = value; }

  get meshWidth() { return this.order.calfCircumferenceWithoutDeduction; }
  set meshWidth(value: number) { this.order.calfCircumferenceWithoutDeduction = value; }

  get meshCircumreference() { return this.order.ankleCircumference; }
  set meshCircumreference(value: number) { this.order.ankleCircumference = value; }

  async onDeleteProduct(model: Model2): Promise<void> {
    const t = (k: string) => this.$t(k) as string;
    const productId = model.product.productId;

    const confirm = (msgKey: string, titleKey: string, okKey: string, cancelKey: string) =>
      ElMessageBox.confirm(t(msgKey), t(titleKey), {
        confirmButtonText: t(okKey),
        cancelButtonText: t(cancelKey),
        type: 'warning',
      });

    try {
      await confirm('confirm.delete.message', 'confirm.delete.title', 'enum.yesNoType.true', 'enum.yesNoType.false');
    } catch {
      return;
    }
    try {
      await confirm('confirm.historyDelete.message', 'confirm.historyDelete.title', 'confirm.delete.ok', 'confirm.delete.cancel');
    } catch {
      return;
    }
    try {
      const result = await modelService.deleteModel(model.modelId);
      if (!result) {
        ElMessage.error(t('error.api.delete'));
        return;
      }
      ElMessage.success(t('success.api.delete'));
      this.productList = this.productList.filter(
        (p) => p.product.productId !== productId
      );
      try {
        await FormBuilderService.deleteFormBuilderAzureFiles(productId);
        FormBuilderService.deleteProductFormBuilder(productId).catch(() => {});
      } catch {
      }
      await deleteFileMetadataToAzureByProudctId(String(productId));
    } catch (e) {
      ElMessage.error(t('error.api.delete'));
    }
  }


  get isAutomaticScanActive(): boolean {
    return !!(this.selectedScan && this.selectedSkeleton && this.selectedScan.fileId && this.selectedSkeleton.fileId);
  }

  // desktop wheel => horizontal scroll
  private wheelToHorizontal = (e: WheelEvent) => {
    const el = this.pickerStrip;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    }
  };
  private setupWheelBinder() {
    const el = this.pickerStrip;
    if (!el) return;

    const isTouch =
      'ontouchstart' in window ||
      (navigator as any).maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;

    if (isTouch) {
      if (this._wheelBound) {
        el.removeEventListener('wheel', this.wheelToHorizontal as any);
        this._wheelBound = false;
      }
      return;
    }
    if (!this._wheelBound) {
      el.addEventListener('wheel', this.wheelToHorizontal, { passive: false });
      this._wheelBound = true;
    }
  }

  async mounted(): Promise<void> {
    const urlProductId = Number(this.route.query.productId);
    const urlProductTypeId = Number(this.route.query.productTypeId);
    const supplier = this.route.query.supplier ?? '';
    this.workflowModel.setStep(1);

    if (this.modelList.length > 0) {
      this.selectModelFromRoute();
    }

    const lastStep = this.steps.length;
    window.onpopstate = () => {
      if (this.workflowModel.active == lastStep) {
        window.location.reload();
      } else if (this.workflowModel.active == lastStep - 1) {
        this.setStep(lastStep - 2);
      } else {
        const stepFromQuery = this.route.query.sp ? parseInt(this.route.query.sp as string, 10) : 1;
        this.setStep(stepFromQuery);
      }
    };

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) this.setStep(1);
    });

    if (this.isTypeCover(urlProductTypeId) && this.workflowModel?.OnClickNextAction && (urlProductId === 5 || urlProductId === 6)) {
      this.workflowModel.OnClickNextAction(2);
    }

    LayoutUtility.refresh();
    if (!this.productTypeId) this.router.replace(`/page/not-found`);

    this.fullscreenLoading = true;
    this.productsLoading = true;

    try {
      this.isUserSupplier = this.userStore.roles.includes(Role.Supplier);
      const userDomain = this.userStore.domain?.urlSuffix ?? 'aidddo';

      let currentLanguage = i18n.global.locale as string;
      if (currentLanguage.length !== 2) currentLanguage = 'de';

      const [orderTemplate, confirmationTemplate] = await Promise.all([
        fetchEmailTemplate(`/email-templates/${userDomain}/order.html`),
        fetchEmailTemplate(`/email-templates/${userDomain}/order-confirmation-${currentLanguage}.html`)
      ]);

      this.orderTemplate.HTMLTemplate = orderTemplate;
      this.orderTemplate.HTMLConfirmationTemplate = confirmationTemplate;

      await this.loadModels();

      if (this.selectedModel) {
        this.handleProductSelected(this.selectedModel);
        this.getCoverCategory(this.selectedModel?.product.name);
      }
    } catch (error) {
      console.error('Failed to load configurator data:', error);
    } finally {
      this.productsLoading = false;
      this.fullscreenLoading = false;
    }

    // Phone media query binding
    const mq = window.matchMedia(this.isPhoneMq);
    const set = () => (this.isPhone = mq.matches);
    set();
    mq.addEventListener?.('change', set);
    window.addEventListener('orientationchange', set);

    this.$nextTick(() => this.setupWheelBinder());
  }

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    try {
      if (this.pickerStrip && this._wheelBound) {
        this.pickerStrip.removeEventListener('wheel', this.wheelToHorizontal as any);
      }
    } catch {}
  }

  onResize = () => (this.width = window.innerWidth);

  @Watch('modelList', { immediate: true })
  onModelListChange(newList: Model2[]) {
    if (newList.length > 0) this.selectModelFromRoute();
  }

  selectModelFromRoute() {
    if (!this.modelList || this.modelList.length === 0) {
      console.warn('Model list is still empty; cannot select model.');
      return;
    }
    const modelFromLS = localStorage.getItem('mlid');
    if (this.route.query.mlid) {
      this.selectedModelId = parseInt(this.route.query.mlid.toString(), 10);
    } else {
      this.selectedModelId = Number(modelFromLS);
    }
    const selectedModel = this.modelList.find((model: Model2) => model.modelId === this.selectedModelId);
    if (selectedModel) {
      this.selectedModel = selectedModel;
      this.handleProductSelected(this.selectedModel);
      this.getCoverCategory(this.selectedModel.product.name);
    } else {
      console.warn(`Model with ID ${this.selectedModelId} not found.`);
    }
  }

  @Watch('$route.query.productTypeId')
  onProductTypeChange(newVal: string | undefined, oldVal: string | undefined) {
    if (newVal && newVal !== oldVal) this.loadModels();
  }

  @Watch('workflowModel.active', { immediate: true })
  onWorkflowStepChange(workflowStep: number | null | undefined) {
    if (workflowStep !== null && typeof workflowStep !== 'undefined') {
      this.setStep(workflowStep);
      this.router.push({
        path: this.route.path,
        query: { ...this.route.query, sp: workflowStep.toString() },
      });
    } else {
      this.setStep(1);
    }

    if (workflowStep === 3) {
      if (this.meshCountItemList.length <= 0) this.updateMeshCount();
      this.$nextTick(() => {
        this.updateScrollListener();
        this.restoreScrollPosition();
        this.setupWheelBinder();
      });
    }

    const lastStep = this.steps.length;
    if (workflowStep === lastStep - 1) {
      const generatedIdNumber = generateIDNumber('O');
      localStorage.setItem('generatedIdNumber', generatedIdNumber);
    }
  }

  updated() {
    this.updateScrollListener();
    this.restoreScrollPosition();
    this.$nextTick(() => this.setupWheelBinder());
  }
  updateScrollListener() {
    this.colorAndDesignContainer = (this.$refs.colorAndDesignContainer as HTMLElement) || null;
  }
  private storeScrollPosition() {
    if (this.colorAndDesignContainer) {
      this.lastScrollPosition = this.colorAndDesignContainer.scrollTop;
    }
  }
  private restoreScrollPosition() {
    this.$nextTick(() => {
      if (this.colorAndDesignContainer) {
        this.colorAndDesignContainer.scrollTop = this.lastScrollPosition;
      }
    });
  }

  checkParameters() {
    if (this.productId && this.productId < this.modelList.length) {
      this.workflowModel.OnClickNextAction(1);
    }
    if (this.embossingId) {
      this.setStep(2);
    } else if (this.wtransferId) {
      this.setStep(1);
    }
  }

  getProducts(models: Model2[]) {
    const productList: Model2[] = [];
    for (const model of models) {
      if (productList.findIndex((pt) => pt.product.productId == model.product.productId) === -1) {
        const newModel = cloneDeep(model);
        const sampleImagePaths = model.product.sampleImagePaths?.map((s) => s.content);
        const thumbnailContent = model.product.thumbnail.content;
        if (!sampleImagePaths?.includes(thumbnailContent) && sampleImagePaths) {
          newModel.product = { ...model.product, sampleImagePaths: [...model.product.sampleImagePaths, model.product.thumbnail] } as Product;
        }
        productList.push(newModel);
      }
    }
    this.productList = productList;
  }

  created(): void {
    window.addEventListener('resize', this.resize);
  }
  resize(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  showOrderModal(): void { this.orderModal?.show(); }
  hideOrderModal(): void { this.orderModal?.hide(); }

  updateProductListState({ productId, name }: { productId: number; name: string }): void {
    this.productList = cloneDeep(this.productList).map((p) => {
      if (p.product.productId === productId) p.product.name = name;
      return p;
    });
  }

  async showProductParameterModalDialog(model: Model2 | null, isEmbossing?: boolean): Promise<void> {
    if (model !== null && model !== undefined) {
      this.selectedModel = model;
      if (model.product.productId.toString() !== this.videoMetadata.productId) {
        const metadataFiles = await getFileMetadataToAzureByProudctId(model.product.productId.toString());
        const video = metadataFiles?.find((item) => Object.values(FileTypeVideo).includes(item.fileType as FileTypeVideo));
        if (video) this.videoMetadata = video;
      }
    }
    this.productParameterModalDialog?.show();
    this.productParameterEmbossing = Boolean(isEmbossing);
  }

  loadModels(): void {
    const domain = this.authentication.primaryDomain;
    if (this.authentication.user) {
      modelService
        .getModelsByUserId({ userId: this.authentication.user.userId, productTypeId: this.productTypeId })
        .then((models) => {
          if (models) {
            const mList = models.filter((item) => !item.basedOn);
            this.modelList = mList;
            this.getProducts(mList);
            if (this.productList.length === 1) this.selectedModel = this.productList[0];
          } else {
            this.getProducts([]);
            this.modelList = [];
          }
          this.dataLoaded = true;
          this.checkParameters();
        })
        .finally(() => {
          this.productsLoading = false;
        });

      this.order.technicianFirstname = this.authentication.user.firstname;
      this.order.technicianLastname = this.authentication.user.lastname;
      this.order.mail = this.authentication.user.email;
    } else if (domain || this.supplierUrl) {
      modelService
        .getModelsByUrlSuffix(this.supplierUrl ?? domain?.urlSuffix ?? '', this.productTypeId)
        .then((models) => {
          if (models) {            
            const mList = models.filter((item) => !item.basedOn);
            this.modelList = mList;
            this.getProducts(mList);
            if (this.productList.length === 1) this.selectedModel = this.productList[0];
          } else {
            this.getProducts([]);
            this.modelList = [];
          }
          this.dataLoaded = true;
          this.checkParameters();
        })
        .finally(() => {
          this.productsLoading = false;
        });
    } else {
      this.router.push(`/page/not-found`);
    }
  }

  loadTemplateFromDB(): void {
    this.meshEditorLoading = true;
    this.abortController = new AbortController();
    this.meshTreeData.clear();
    TemplateService.getTemplateData(this.selectedModelId!, ProductQuality[this.selectedProductQuality as keyof typeof ProductQuality], this.abortController)
      .then((templateData) => {
        this.loadTemplate(templateData);
      })
      .catch(() => {
        this.meshEditorLoading = false;
      });
  }

  loadTemplate(templateData: Model2) {
    this.meshEditorLoading = true;
    if (this.selectedModelId) {
      this.selectedModel = templateData;
      if (templateData && templateData?.components?.length > 0) {
        const loadChildren = (parent: string | MeshTreeDataItem | null, children: Component1[]): void => {
          for (const child of children) {
            const level = Object.keys(ProductQuality).indexOf(this.selectedProductQuality);
            const data = modelService.parseToMeshExportData(child, child.resolutions[level] ?? child.resolutions[0]);
            let treeData!: MeshTreeDataItem;
            if (data.fileType !== FileType3D.NONE) {
              treeData = this.meshTreeData.addBase64Import(
                data.uniqueKey,
                data.componentId,
                data.name,
                data.fileType,
                data.base64,
                data.thumbnailType,
                data.thumbnail,
                parent,
                false,
                data.color,
                data.position,
                data.rotation,
                data.scale
              );
              treeData.bones = data.bones.map((boneInfo) => {
                const bone = new THREE.Bone();
                bone.name = boneInfo.name;
                bone.position.copy(boneInfo.position);
                return bone;
              });
            } else {
              treeData = this.meshTreeData.addGroup(
                data.name,
                [],
                data.position,
                data.rotation,
                data.scale,
                parent || (this.$t(`enum.upload-category.${data.category}`) as string),
                false
              );
              treeData.id = data.uniqueKey;
            }
            loadChildren(
              treeData,
              templateData.components.filter((item) => item.parent?.uuid === data.uniqueKey)
            );
          }
        };
        loadChildren(
          null,
          templateData.components.filter((item) => item.parent === null)
        );

        if (this.loadedScan) {
          loadChildren(null, [this.loadedScan]);
        }
      }
    }
  }

  componentToHex(c: number): string {
    const hex = c.toString(16);
    return hex.length == 1 ? '0' + hex : hex;
  }
  rgbToHexString(value: RGB): string {
    return '#' + this.componentToHex(value.red) + this.componentToHex(value.green) + this.componentToHex(value.blue);
  }

  get productCount(): number { return this.productDB.length; }
  get productPagesCount(): number { return Math.ceil(this.modelList.length / this.productsPerPage); }

  @Watch('window.innerWidth', { immediate: true })
  getProductsForPage(page: number): Model2[] {
    return this.modelList.slice((page - 1) * this.productsPerPage, page * this.productsPerPage);
  }
  get productsPerPage(): number { return 50; }

  // @Watch('meshTreeData.lastUpdateVisibility', { immediate: true })
  // selectionChanged(): void {}

  toggleSidemenu() {
    this.isSidemenu = !this.isSidemenu;
    const sidemenu = document.getElementById('sidemenu');
    if (this.isSidemenu) sidemenu?.classList.add('expanded');
    else sidemenu?.classList.remove('expanded');
  }

  async setStep(step: number) {
    const prodName = (this.selectedModel?.product.name || '').toLowerCase();
    const isScover = step === 2 && this.isTypeCover(this.productTypeId) && prodName.includes('s-cover');
    if (isScover) {
      this.workflowModel.setStep(3);
      return;
    }

    const isForwardStep = step > this.workflowModel.active;
    if (isForwardStep) {
      if (this.meshEditorLoadingProps) {
        ElMessage.error(this.$t('error.vuelidate.modelLoading'));
        return;
      }
      if (this.workflowModel.active === 1 && !this.validateFirstStep()) return;

      const needsSecondStepValidation =
        !this.secondStepValid && (this.workflowModel.active === 2 || step === 3);

      if (needsSecondStepValidation) {
        const valid = await this.validateSecondStep();
        if (!valid) return;
        this.secondStepValid = true;
      }
    }

    const final = Math.min(step, this.steps.length);
    this.workflowModel.setStep(final);
  }

  async nextStep() {
    const name = (this.selectedModel?.product.name || '').toLowerCase();
    if (
        this.workflowModel.active === 1 && this.isTypeCover(this.productTypeId) &&
        name.includes('s-cover')
    ) {
      await this.setStep(2);
      return;
    }

    if (this.meshEditorLoadingProps) {
      return ElMessage.error(this.$t('error.vuelidate.modelLoading'));
    }
    if (this.workflowModel.active === 1) {
      const ok = await this.validateFirstStep();
      if (!ok) return;
    }
    if (this.workflowModel.active === 2) {
      const ok = await this.validateSecondStep();
      if (!ok) return;
    }

    const next = Math.min(this.workflowModel.active + 1, this.steps.length);
    await this.setStep(next);
  }

  goToOrder() { this.setStep(4); }
  goToProductTypes() { this.setStep(0); }

  async checkSupplier(email: string) {
    const isSupplier = await UserService.getSupplierByEmail(email ?? '');
    this.isUserSupplier = Boolean(isSupplier);
  }

  async previousStep() {
    const name = (this.selectedModel?.product.name || '').toLowerCase();
    if (
      this.workflowModel.active === 3 && this.isTypeCover(this.productTypeId) &&
      (name.includes('s-cover')) 
    ) {
      await this.setStep(1);
      return;
    }
    const prev = Math.max(this.workflowModel.active - 1, 1);
    await this.setStep(prev);
  }

  handleGenerate(model: Model2) {
    this.generated = true;
    this.handleProductSelected(model);
  }

  convertToUrl(model: Model2): string | null {
    if (model.product && model.product.thumbnail) return model.product.thumbnail.content;
    return null;
  }

  validateFirstStep() {
    const isValid = Boolean(this.selectedModel);
    if (!isValid) ElMessage.error(this.$t('error.vuelidate.required') as string);
    return isValid;
  }

  @Ref() readonly productSettingsRef!: InstanceType<typeof ProductSettings>;
  isFormValid = false;

  setFormValid = (valid: boolean) => { this.isFormValid = valid; };

  async validateSecondStep(): Promise<boolean> {
    if (!this.productSettingsRef?.validateProductForm) return false;
    let isValid = true;
    try {
      const valid = await this.productSettingsRef.validateProductForm();
      if (!valid) isValid = false;
      if (!isValid) ElMessage.error(this.$t('error.vuelidate.validationErrors') as string);
      return isValid;
    } catch (error) {
      console.error('error', error);
      return false;
    }
  }

  handleSelectProductType(product: Product) {
    this.router
      .push({ path: '/', query: { productTypeId: product.type.productTypeId, step: 1 } })
      .then(() => {
        this.loadModels();
        this.nextStep();
        this.selectedModel = null;
        this.selectedModelId = null;
      });
  }
}
</script>

<style scoped lang="scss">
/* =========================================================
   Layout & Resets
========================================================= */
.container-fluid { padding: 0 !important; }

/* Sidemenu layout (vertical flex) */
.sidemenu {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;

  width: 0;
  height: 100%;
  background-color: #fff;
  box-shadow: 1px 0 10px #ccc;
  overflow: hidden;
  transition: width 0.3s ease;
  z-index: 2;

  .sidemenu-header {
    flex: 0 0 auto;
    margin-bottom: 1rem;
    text-align: center;
  }
}

/* Scrollable content area (default) */
.sidemenu-body {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;

  padding-left: 10%;
  padding-right: 10%;
  padding-bottom: 10rem;

  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.cd-collapse > * { font-size: medium; }
.sidemenu-body > * { flex: 0 0 auto; }

/* Drawer sizing */
.sidemenu.expanded { position: relative; width: 25rem; }

@media (max-width: 768px) {
  .sidemenu.expanded { width: 16rem; }
}

.sidemenu.expanded > .collapse-btn { transform: translateX(25rem); }

/* Fade contents while collapsed */
.sidemenu > * {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0s 0s;
}
.sidemenu.expanded > * {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.3s ease, visibility 0s 0s;
}

/* Collapse styles (Element Plus) */
:deep(.el-collapse-item__header) {
  margin-bottom: 0.3rem;
  padding: 1rem;
  background-color: #eaeaea;
  border-bottom: none;
  border-radius: 0.5rem;
}
:deep(.el-collapse) { --el-collapse-border-color: #0000; }
:deep(.el-loading-spinner) {
  position: relative !important;
  justify-self: center !important;
  align-self: center !important;
}

/* Toggle button */
.collapse-btn {
  position: relative;
  align-self: center;
  z-index: 2;

  display: inline-block;
  margin: 0;
  padding: 0.5rem 0.3rem;

  background-color: #fff;
  color: #1d1e19;
  border: none;
  border-radius: 0 5px 5px 0;
  box-shadow: 5px 0 4px #ccc;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.collapse-btn:hover { background-color: #f4f4f4; }


.sidemenu-footer {
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: #fff;
  box-shadow: 0 -5px 8px #dedede;
}

.btn-stepper { margin: 0 !important; min-width: 100px; }

/* =========================================================
   Progress Line
========================================================= */
.workflow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.workflow-line {
  width: 4rem;
  height: 0.5rem;
  margin: 0 3px;
  background-color: #ccc;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}
.workflow-line:hover { background-color: #a6a6a6; cursor: pointer; }
.workflow-line.active { background-color: var(--color-primary); }

/* =========================================================
   Products & Titles
========================================================= */
.title {
  margin: 0;
  padding-left: 1.6rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1d1e19;
}
.product-card__wrapper { width: fit-content; height: fit-content; justify-content: center; }
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  justify-items: center;
  align-items: center;
  gap: 0.8rem;

  max-height: 55dvh;
  padding: 1rem 1.5rem 3rem;
  overflow-y: auto;
  overflow-x: hidden;
}

/* =========================================================
   Phone-only UI (≤ 828px)
========================================================= */
@media (max-width: 828px) {
  .title { padding-left: 0; text-align: center; }

  .sidemenu.is-phone .sidemenu-body.variants-open { overflow-y: hidden; }

  .variant-strip,
  .variant-strip .phone-card--variant,
  .variant-strip .phone-card__body--row,
  .variant-strip .phone-card__text,
  .variant-strip .phone-card__title,
  .variant-strip .phone-card__subtitle,
  .variant-strip .phone-card__desc,
  .variant-strip :deep(img) {
    touch-action: pan-x !important;
    overscroll-behavior-y: none !important;
  }

  .variant-strip { overflow-y: hidden !important; }

  .product-grid { grid-template-columns: 1fr; gap: 0.5rem; }

  .sidemenu.expanded { width: 100vw; }

  .sidemenu-body { padding-bottom: 5.5rem; }

  .sidemenu-footer {
    position: fixed;
    left: 0; right: 0; bottom: env(safe-area-inset-bottom);
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
  }

  .sidemenu.is-phone.expanded {
    position: fixed; left: 0; right: 0; bottom: 0;
    width: 100vw;
    height: calc(50vh + env(safe-area-inset-bottom));
    border-top-left-radius: 16px; border-top-right-radius: 16px;
    box-shadow: 0 -12px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden; z-index: 1000;
  }

  .sidemenu.is-phone { overflow-y: hidden; overscroll-behavior: none; }

  .sidemenu.is-phone .sidemenu-header {
    position: sticky; top: 0; z-index: 2;
    padding-top: 0.75rem; padding-bottom: 0.5rem;
    background: #fff;
    border-top-left-radius: 16px; border-top-right-radius: 16px;
  }

  .sidemenu.is-phone .sidemenu-body {
    height: auto;
    max-height: calc(50vh - 3.5rem - 56px);
    padding-left: 0.75rem; padding-right: 0.75rem; padding-bottom: 5.5rem;
    overflow-y: auto; -webkit-overflow-scrolling: touch;
  }

  .sidemenu.is-phone .sidemenu-footer {
    position: absolute; left: 0; right: 0; bottom: 0;
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
    border-top-left-radius: 12px; border-top-right-radius: 12px;
  }

  .sidemenu.is-phone + .collapse-btn { display: none; }

  .phone-category-chips {
    position: sticky; top: 0; z-index: 3;
    display: flex; gap: 0.5rem;
    padding: 0.5rem 0.75rem 0.25rem; margin-bottom: 0.25rem;
    background: #fff; overflow-x: auto; -webkit-overflow-scrolling: touch;

    .chip {
      -webkit-tap-highlight-color: transparent;
      flex: 0 0 auto;
      padding: 0.4rem 0.7rem;
      border: 1px solid #e5e5e5; border-radius: 999px;
      font-size: 0.9rem; line-height: 1; background: #fff;
    }
    .chip--active { border-color: var(--color-primary); background: rgba(0, 0, 0, 0.02); }
  }

  .phone-card-list {
    display: grid; grid-template-columns: 1fr;
    gap: 0.75rem; padding: 0.5rem 0.75rem 5.5rem;
  }

  .phone-card {
    background: #fff; border-radius: 12px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06); overflow: hidden;

    &--active { outline: 2px solid var(--color-primary); outline-offset: 0; }

    .phone-card__body {
      width: 100%; display: flex; gap: 0.9rem; padding: 0;
      text-align: left; background: transparent; border: 0;
    }

    :deep(img) { display: block; width: 96px; height: 96px; object-fit: cover; }

    .phone-card__text {
      display: grid; align-content: center; gap: 0.15rem;
      min-height: 96px; padding: 0.8rem 0.8rem 0.8rem 0;
    }

    .phone-card__title { margin: 0; font-size: 1rem; font-weight: 700; color: #1d1e19; }
    .phone-card__subtitle { margin: 0; font-size: 0.82rem; opacity: 0.7; }
    .phone-card__desc { margin: 0.15rem 0 0; font-size: 0.85rem; line-height: 1.25; color: #444; }
  }

  .variant-strip {
    display: grid; grid-auto-flow: column; grid-auto-columns: 78%;
    gap: 0.75rem; padding: 0.5rem 0.75rem 5rem;

    overflow-x: auto; overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
    touch-action: pan-x !important;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: none !important;
  }

  .phone-card--variant { border-radius: 14px; scroll-snap-align: start; }
  .phone-card__body--row { display: flex; align-items: stretch; gap: 2.2rem; padding: 2.5rem; }

  .variant-strip :deep(img) {
    display: block; width: 112px; height: 112px; object-fit: cover;
    border-radius: 10px; flex: 0 0 112px;
  }

  .variant-strip .phone-card__text {
    min-height: 112px; min-width: 0;
    display: flex; flex-direction: column; justify-content: center;
    padding: 0; overflow: hidden;
  }

  .variant-strip .phone-card__title   { font-size: 1rem; }
  .variant-strip .phone-card__subtitle{ font-size: 0.82rem; }
  .variant-strip .phone-card__desc    { font-size: 0.84rem; }

  .variant-thumb {
    flex: 0 0 128px; width: 128px;
    display: flex; align-items: center; justify-content: center;
    margin-right: 0.05rem;
  }
  .variant-thumb :deep(img) { width: 100%; height: 128px; object-fit: cover; border-radius: 10px; margin-right: 0; }
}

/* =========================================================
   Desktop (≥ 829px) — footer fixed at bottom, same width as drawer
========================================================= */
@media (min-width: 829px) {
  .sidemenu {
    position: relative;
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
  }

  .sidemenu .sidemenu-body {
    flex: 1 1 auto;
    overflow: visible;
    max-height: none;
  }

  /* Show the footer only when the drawer is open and
     make it exactly as wide as the drawer */
  .sidemenu.expanded .sidemenu-footer {
    position: fixed;
    bottom: 0;
    left: 80px;          /* align with the drawer’s left edge (adjust if your drawer isn’t at 0) */
    width: 25rem;     /* same as .sidemenu.expanded width */
    z-index: 1000;
  }

  .product-grid,
  .phone-card-list {
    max-height: none !important;
    overflow: visible !important;
  }
}



/* PHONE — horizontal swatch/embossing scroller */
@media (max-width: 828px) {
  .picker-strip {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: none;

    /* Element Plus Scrollbar case */
    :deep(.el-scrollbar__wrap) {
      overflow-x: auto !important;
      overflow-y: hidden !important;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-x !important;
    }
    :deep(.el-scrollbar__view) {
      display: inline-flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      gap: .75rem !important;
      width: max-content !important;
    }

    /* Common fallback rows/lists */
    :deep(.el-row),
    :deep(.row),
    :deep(.swatch-row),
    :deep(.picker-row),
    :deep(.chips),
    :deep(.swatch-list),
    :deep(.texture-list),
    :deep(.embossing-list),
    :deep(.color-list),
    :deep(ul),
    :deep(ol) {
      display: inline-flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      align-items: stretch !important;
      gap: .75rem !important;
      width: max-content !important;
      margin: 0 !important;
      padding: .25rem .25rem .75rem !important;
      overflow-x: visible !important;
      overflow-y: hidden !important;
      scroll-snap-type: x proximity;
    }

    /* tiles */
    :deep(.el-col),
    :deep(li),
    :deep(.swatch),
    :deep(.chip),
    :deep(button),
    :deep(.picker-item),
    :deep(.el-radio-button),
    :deep(.el-checkbox-button),
    :deep(.el-card) {
      flex: 0 0 auto !important;
      min-width: 64px !important;
      min-height: 64px !important;
      scroll-snap-align: start;
    }
  }

  /* part chips */
  .part-supermenu {
    position: sticky; top: 0; z-index: 3;
    display: flex; gap: .5rem; padding: .5rem .75rem .25rem;
    background: #fff; overflow-x: auto; -webkit-overflow-scrolling: touch;
  }
  .part-chip {
    -webkit-tap-highlight-color: transparent;
    flex: 0 0 auto; padding: .4rem .7rem;
    border: 1px solid #e5e5e5; border-radius: 999px; background:#fff;
    font-size: .9rem; line-height: 1;
  }
  .part-chip--active { border-color: var(--color-primary); background: rgba(0,0,0,.02); }

  /* allow page to scroll vertically outside the strip */
  .sidemenu.is-phone .sidemenu-body {
    touch-action: pan-y;
    overscroll-behavior-x: contain;
  }
  /* PHONE: allow vertical scroll + wrap color lists into multiple rows */
  @media (max-width: 828px) {
    /* let vertical scrolling work again */
    .picker-strip {
      touch-action: auto;            /* was pan-x */
      overscroll-behavior-y: auto;   /* was none */
      overflow-y: visible;           /* was hidden */
    }

    /* make colors/embossing/texture wrap into multiple rows */
    .picker-strip :deep(.color-list),
    .picker-strip :deep(.embossing-list),
    .picker-strip :deep(.texture-list) {
      display: flex !important;
      flex-wrap: wrap !important;    /* <-- multi-rows */
      gap: .5rem !important;
      width: 100% !important;        /* let them break onto new lines */
    }

    /* optional: keep tiles a consistent size */
    .picker-strip :deep(.color-list > *),
    .picker-strip :deep(.embossing-list > *),
    .picker-strip :deep(.texture-list > *) {
      width: 56px;
      height: 56px;
      flex: 0 0 auto;
    }
  }

}
</style>
